import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import {
  X,
  Search,
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Inbox,
  Loader2,
  ArrowRight,
} from "lucide-react";
import FilterBuilderComponent from "./FilterBuilderComponent";
import "./ReferenceModal.css";

const ReferenceModal = ({
  isOpen,
  title,
  referenceTable,
  displayField,      // optional hint for display
  onSelect,          // will be called with { id, display_value }
  onClose,
  token,
  apiBaseUrl,
  viewType = "list",
  tableFieldId,      // table_fields.id of the *referencing* field -- when set,
                      // the search is scoped by that field's reference_qualifier_script
  currentRecord,     // the in-progress record the picker was opened from, for the qualifier script to read
}) => {
  const [qualifierActive, setQualifierActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [advancedFilter, setAdvancedFilter] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [fields, setFields] = useState([]);
  const [activeViewColumns, setActiveViewColumns] = useState(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const displayedFields = activeViewColumns
    ? fields
        .filter((f) => activeViewColumns[f.name])
        .sort((a, b) => activeViewColumns[a.name].order - activeViewColumns[b.name].order)
    : fields.filter((f) => f.name !== "id");

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !referenceTable) return;

    // fresh state on open
    setColumnFilters({});
    setSortBy(null);
    setSortOrder("asc");
    setOffset(0);
    setAdvancedFilter(null);
    setSelectedRowId(null);

    (async () => {
      try {
        // table metadata -> id
        const tablesRes = await axios.get(`${apiBaseUrl}/tables/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const table = tablesRes.data.find((t) => t.name === referenceTable);
        if (!table) return;

        // fields
        const fieldsRes = await axios.get(
          `${apiBaseUrl}/search/table_fields/?table_id=${table.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFields(fieldsRes.data.records || []);

        // views
        const viewsRes = await axios.get(
          `${apiBaseUrl}/search/views/?sip_table=${table.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const listViews = (viewsRes.data.records || []).filter(
          (v) => v.sip_view_type === viewType
        );
        const def = listViews.find((v) => v.sip_is_default) || listViews[0];

        if (def) {
          const colsRes = await axios.get(
            `${apiBaseUrl}/search/view_columns/?sip_view_id=${def.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const map = {};
          (colsRes.data.records || []).forEach((c, i) => {
            map[c.sip_field_name] = { order: c.sip_column_order ?? i };
          });
          setActiveViewColumns(map);
        } else {
          // fallback: first 8 meaningful fields
          const fallback = (fieldsRes.data.records || [])
            .filter((f) => f.name !== "id")
            .slice(0, 8)
            .reduce((acc, f, i) => {
              acc[f.name] = { order: i };
              return acc;
            }, {});
          setActiveViewColumns(fallback);
        }
      } catch (e) {
        console.error("RefModal: failed metadata load", e);
      }
    })();
  }, [isOpen, referenceTable, token, apiBaseUrl, viewType]);

  useEffect(() => {
    if (!isOpen || !referenceTable || !fields.length) return;

    const params = new URLSearchParams();

    Object.entries(columnFilters).forEach(([key, val]) => {
      if (val) params.set(`${key}__contains`, val);
    });

    params.set("limit", limit);
    params.set("offset", offset);

    if (sortBy) {
      params.set("sort_by", sortBy);
      params.set("sort_order", sortOrder);
    }

    if (advancedFilter && typeof advancedFilter === "object") {
      try {
        params.set("filter_json", JSON.stringify(advancedFilter));
      } catch (e) {
        console.error("Failed to stringify advancedFilter", e);
      }
    }

    if (tableFieldId) {
      params.set("qualifier_field_id", tableFieldId);
      try {
        params.set("current_record", JSON.stringify(currentRecord || {}));
      } catch (e) {
        console.error("Failed to stringify currentRecord", e);
      }
    }

    setLoadingRows(true);
    axios
      .get(`${apiBaseUrl}/search/${referenceTable}/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setRows(res.data.records || []);
        setTotalRecords(res.data.total || 0);
        setQualifierActive(!!res.data.qualifier_active);
      })
      .catch((err) => {
        console.error("Failed to fetch reference data:", err);
      })
      .finally(() => setLoadingRows(false));
  }, [
    isOpen,
    fields,
    columnFilters,
    sortBy,
    sortOrder,
    offset,
    limit,
    referenceTable,
    advancedFilter,
    token,
    apiBaseUrl,
    tableFieldId,
    currentRecord,
  ]);

  const getDisplayFieldName = () => {
    if (displayField) return displayField;
    const df = fields.find((f) => f.display);
    if (df) return df.name;
    // fallback to first rendered column
    return displayedFields[0]?.name || "id";
  };

  const getDisplayValue = (row) => {
    const df = getDisplayFieldName();
    const val = row?.[df];
    if (val === undefined || val === null) return String(row?.id ?? "");
    if (typeof val === "object" && val.display_value) return val.display_value;
    return String(val);
  };

  const formatValue = (value, field) => {
    if (value === null || value === undefined) return <span className="rm-bool-no">—</span>;
    const type = (field.field_type || "").toUpperCase();

    if (typeof value === "object" && value.display_value) {
      return value.display_value;
    }
    if (type.includes("DATE") || type.includes("TIME")) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString();
      }
    }
    if (type.includes("BOOL")) {
      return value ? (
        <span className="rm-bool-yes"><Check size={13} /></span>
      ) : (
        <span className="rm-bool-no">—</span>
      );
    }
    return String(value);
  };

  const commitSelection = useCallback(
    (row) => onSelect({ id: row.id, display_value: getDisplayValue(row) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelect, fields, displayField]
  );

  if (!isOpen) return null;

  const pageCount = Math.max(1, Math.ceil((totalRecords || 0) / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  return ReactDOM.createPortal(
    <div className="rm-overlay" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-header">
          <div className="rm-header-titles">
            <span className="rm-header-icon">
              <Search size={17} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="rm-title">{title}</div>
              <div className="rm-caption">Click a row to preview, then Select — or double-click to choose it</div>
            </div>
          </div>
          <button onClick={onClose} className="rm-close" title="Close (Esc)">
            <X size={17} />
          </button>
        </div>

        <div className="rm-toolbar">
          <span className="rm-toolbar-count">
            <strong>{totalRecords}</strong> record{totalRecords === 1 ? "" : "s"}
          </span>
          {qualifierActive && (
            <span className="rm-toolbar-count" title="This list is scoped by a reference qualifier script configured on this field">
              Filtered by field qualifier
            </span>
          )}
          <button
            onClick={() => setShowFilterBar((s) => !s)}
            className={`sn-btn ${showFilterBar ? "sn-btn--primary" : "sn-btn--neutral"}`}
          >
            <SlidersHorizontal size={14} />
            {showFilterBar ? "Hide Filters" : "Advanced Filter"}
          </button>
        </div>

        {showFilterBar && (
          <div className="rm-filter-panel">
            <FilterBuilderComponent
              filterTable={referenceTable}
              onChange={(val) => {
                try {
                  const parsed = typeof val === "string" ? JSON.parse(val) : val;
                  setAdvancedFilter(parsed);
                } catch (e) {
                  console.error("Invalid filter JSON", e);
                }
                setOffset(0);
              }}
              token={token}
              apiBaseUrl={apiBaseUrl}
              isEmbeddedInListView={true}
            />
          </div>
        )}

        <div className="rm-table-wrap">
          {loadingRows && (
            <div className="rm-loading-overlay">
              <Loader2 size={26} className="rm-spinner" />
            </div>
          )}

          <table className="rm-table">
            <thead>
              <tr>
                {displayedFields.map((field) => (
                  <th key={field.name} className="rm-th">
                    <div
                      className="rm-th-sort"
                      onClick={() => {
                        if (sortBy === field.name) {
                          setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
                        } else {
                          setSortBy(field.name);
                          setSortOrder("asc");
                        }
                      }}
                    >
                      {field.label}
                      {sortBy === field.name &&
                        (sortOrder === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                    </div>
                    <input
                      type="text"
                      placeholder="Filter…"
                      value={columnFilters[field.name] || ""}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === "Enter" && setOffset(0)}
                      className="rm-th-filter"
                    />
                  </th>
                ))}
                <th className="rm-th" style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={`rm-row${selectedRowId === row.id ? " is-selected" : ""}`}
                  onClick={() => setSelectedRowId(row.id)}
                  onDoubleClick={() => commitSelection(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitSelection(row);
                  }}
                  tabIndex={0}
                >
                  {displayedFields.map((f) => (
                    <td key={f.name} className="rm-td">
                      {formatValue(row[f.name], f)}
                    </td>
                  ))}
                  <td className="rm-td rm-td-action">
                    <button
                      type="button"
                      className="rm-select-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        commitSelection(row);
                      }}
                    >
                      Select <ArrowRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {!loadingRows && rows.length === 0 && (
                <tr>
                  <td colSpan={displayedFields.length + 1} className="rm-empty">
                    <div className="rm-empty-icon">
                      <Inbox size={20} />
                    </div>
                    No matching records
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rm-footer">
          <div className="rm-footer-group">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setOffset(0);
              }}
              className="rm-page-select"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="rm-pager">
            <button
              onClick={() => setOffset((p) => Math.max(0, p - limit))}
              disabled={offset === 0}
              className="rm-pager-btn"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span>
              Page <strong>{currentPage}</strong> of <strong>{pageCount}</strong>
            </span>
            <button
              onClick={() => {
                const next = offset + limit;
                if (next < totalRecords) setOffset(next);
              }}
              disabled={offset + limit >= totalRecords}
              className="rm-pager-btn"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>

          <div className="rm-footer-group">Total: <strong>{totalRecords}</strong></div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReferenceModal;
